import type { IconName } from '@webapp2/shared/components/icons/material-icon'
import { userSlimId, type Flow } from '@webapp2/shared/model'
import { authenticateFinished } from '@webapp2/shared/testing/authenticate-finished'
import { registerNavigationLoading } from './navigation-loading'
import {
  createRouter,
  createWebHistory,
  type RouteLocation,
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric,
  type RouteLocationRaw,
  type RouteParamsGeneric,
  type RouteMeta,
  type RouteRecordMultipleViews,
  type RouteRecordMultipleViewsWithChildren,
  type RouteRecordRaw,
  type RouteRecordRedirect,
  type RouteRecordSingleView,
  type RouteRecordSingleViewWithChildren
} from 'vue-router'
import { ensureLinkedPlayerBuildsSettings } from './admin/services/admin-builds-service'
import { initialAdminUpdate, setAdminStateFromCache } from './admin/services/admin-entity-service'
import { handleAdminNavigation } from './admin/services/admin-service'
import { ensureUser, ensureUserSlims, loadHealthStreamStudents } from './admin/services/admin-user-service'
import { adminState, type AdminPlayerRun } from './admin/state'
import { config, updateGlobalServiceWorker } from './main'
import { postGetBody } from '@webapp2/shared/services/api'
import { handleNavigation as handleAppNavigation } from './services/app-service'
import { authenticate } from './services/authentication-service'
import { currentUserCoursePerformance, registerModuleBuildForCourse } from './services/course-service'
import { initialUpdate, setStateFromCache, updateEntities } from './services/entity-service'
import { isSurveyAvailable, recordSurveyPrompt } from './services/survey-service'
import { ensureModulesRegistered } from './services/launch-service'
import {
  checkForValidAuthCode,
  handleHealthStreamAuthCode,
  loginWithHealthstream
} from './services/healthstream-service'
import {
  ensureLinkedModuleBuilds,
  ensureLinkedModuleBuildsByModuleBuildId,
  linkedModuleBuildsByModuleBuildId
} from './services/linked-module-builds-service'
import {
  ensureCurrentLinkedPlayerBuilds,
  ensureLinkedPlayerBuilds,
  ensureLinkedPlayerBuildsByPlayerBuildId,
  findEmbeddedModule,
  linkedPlayerBuildsByPlayerBuildId
} from './services/linked-player-builds-service'
import { hasAdminPermission, isAdmin } from './services/permissions-service'
import { ensurePlayerTechniques } from './services/player-technique-service'
import { initQuestBridge, shouldRouteToPermissionsDenied } from './services/quest-bridge'
import { prebriefId, state, type PlayerTechnique, type Prebrief } from './state'

export type Layout = 'app' | 'admin' | 'auth'
export type BriefSection = 'prebrief' | 'debrief'

export type NavItem = {
  label: string
  icon: IconName
  route: RouteLocationRaw
  hide?: boolean
  testId: string
  completed?: boolean
  badge?: string
}
export type OssoMeta = {
  showNavInVuplex?: boolean
  navItems?: (route: RouteLocationNormalizedLoadedGeneric) => NavItem[]
  hideOnWeb?: boolean
  fillHeight?: boolean
  tabs?: boolean
  loader?: (to: RouteLocationNormalizedGeneric) => void | RouteLocationRaw | Promise<void | RouteLocationRaw>
}
export type CourseMeta = OssoMeta & {
  icon: IconName
  title: string
  nextText?: string
  hidden?: boolean
  showSkip?: boolean
  hideRetry?: boolean
  section: BriefSection
}
export type AdminMeta = OssoMeta & {
  icon: IconName
  title: string
  hide?: () => boolean
  badge?: string
}

declare module 'vue-router' {
  interface RouteMeta extends OssoMeta {
    layout?: Layout
    externalAuth?: boolean
    backRoute?: RouteLocationRaw
  }
}

type OssoRouteMeta = { meta?: OssoMeta | CourseMeta | AdminMeta }

export type OssoRoute =
  | (Omit<RouteRecordSingleView, 'meta'> & OssoRouteMeta)
  | (Omit<RouteRecordSingleViewWithChildren, 'meta' | 'children'> & OssoRouteMeta & { children: OssoRoute[] })
  | (Omit<RouteRecordMultipleViews, 'meta'> & OssoRouteMeta)
  | (Omit<RouteRecordMultipleViewsWithChildren, 'meta' | 'children'> & OssoRouteMeta & { children: OssoRoute[] })
  | (Omit<RouteRecordRedirect, 'meta'> & OssoRouteMeta)

export type OssoCourseRoute = Omit<OssoRoute, 'meta'> & {
  meta: CourseMeta
}
export type OssoAdminRoute = Omit<OssoRoute, 'meta'> & {
  meta: AdminMeta
  name: string
}

export type OssoRouteLocation = Omit<RouteLocation, 'name'> & {
  name: string
}

export type AdminRouteLocation = Omit<OssoRouteLocation, 'meta'> & {
  meta: RouteMeta & AdminMeta
}

export type OrganizationRouteParams = { organizationId: string }
export type IdRouteParams = OrganizationRouteParams & { id: string }
export type CourseRouteParams = OrganizationRouteParams & { courseId: string }

export type CourseRouteLocation = Omit<OssoRouteLocation, 'meta'> & {
  meta: RouteMeta & CourseMeta
  params: CourseRouteParams
}
export type DebriefRouteLocation = Omit<CourseRouteLocation, 'params'> & {
  params: CourseRouteParams & { runId: string }
}
export type IdRouteLocation = OssoRouteLocation & { params: IdRouteParams }

const orgPrefix = '/org/:organizationId'

export const appRoutes = createRoutes('app', {
  root: {
    path: '/',
    name: 'root',
    component: () => import('./views/CourseListView.vue'),
    beforeEnter: () => {
      if (state.organizationId) {
        return { name: 'courseList', params: { organizationId: state.organizationId } }
      } else {
        return { name: 'login' }
      }
    }
  },
  courseList: {
    path: orgPrefix,
    component: () => import('./views/CourseListView.vue'),
    meta: {
      fillHeight: true,
      loader: () => updateEntities(true)
    }
  },
  performance: {
    path: `${orgPrefix}/performance`,
    component: () => import('./views/MyPerformance.vue')
  },
  manageDownloads: {
    path: `${orgPrefix}/manage-downloads`,
    component: () => import('./views/ManageDownloadsView.vue'),
    beforeEnter: async () => {
      await initialUpdate
      if (!state.user?.isOsso || !state.vuplex) {
        return { name: 'root' }
      }
    }
  },
  collab: {
    path: `${orgPrefix}/collaborative`,
    component: () => import('./views/CollaborativeView.vue'),
    meta: { hideOnWeb: true }
  },
  host: {
    path: `${orgPrefix}/host`,
    component: () => import('./views/HostView.vue'),
    meta: { hideOnWeb: true }
  },
  join: {
    name: 'join',
    path: `${orgPrefix}/join/:id`,
    component: () => import('./views/JoinView.vue'),
    meta: {
      hideOnWeb: true,
      loader: async to => {
        await initialUpdate
        const session = state.multiplayerSessions[to.params.id as string]
        const link = session ? state.linkedModuleBuilds[session.linkedModuleBuildsId] : undefined
        if (link) {
          ensureModulesRegistered([link])
          await ensurePlayerTechniques([link.techniqueId])
        }
      }
    }
  },
  prebrief: {
    path: `${orgPrefix}/courses/:courseId`,
    components: {
      default: () => import('./layout/CourseLayout.vue'),
      pageHeader: () => import('./views/prebrief-section/PrebriefHeader.vue'),
      pageFooter: () => import('./components/CourseFooter.vue'),
      leftNavFooter: () => import('./components/CourseBuildDialogs.vue')
    },
    name: 'prebrief',
    meta: {
      showNavInVuplex: true,
      navItems: route => courseNavItems('prebrief', route),
      loader: async to => {
        await initialUpdate
        const courseParam = to.params.courseId as string
        const isHealthStream = to.query['healthstream']
        const courseId = isHealthStream
          ? Object.values(state.courses).find(x => x.healthStreamCourseId == courseParam)?.id
          : courseParam
        const organizationId = isHealthStream ? state.organizationId : to.params.organizationId
        const course = state.courses[courseId!]
        if (!course) {
          return { name: 'courseList', params: { organizationId } }
        }
        registerModuleBuildForCourse(course.id)

        if (isHealthStream) {
          return { name: to.name, params: { organizationId, courseId }, query: {} }
        }
      }
    },
    redirect: () => {
      return { name: 'prebrief-welcome' }
    },
    children: createCourseChildren('prebrief', [
      {
        path: 'welcome',
        name: 'prebrief-welcome',
        component: () => import('./views/prebrief-section/WelcomeView.vue'),
        meta: { icon: 'home', title: 'Welcome', nextText: "Let's start this journey!" }
      },
      {
        path: 'learning',
        name: 'prebrief-learning-objectives',
        component: () => import('./views/prebrief-section/LearningObjectivesView.vue'),
        meta: { icon: 'school-cap', title: 'Learning Objectives', nextText: "Great! What's next?" }
      },
      {
        path: 'briefing',
        name: 'prebrief-scenario-briefing',
        component: () => import('./views/prebrief-section/ScenarioBriefingView.vue'),
        meta: { icon: 'clip-board', title: 'Scenario Briefing', nextText: "I've got it!" }
      },
      {
        path: 'scenario',
        name: 'prebrief-scenario-details',
        component: () => import('./views/prebrief-section/ScenarioDetailsView.vue'),
        meta: { icon: 'bulb', title: 'Scenario Details', nextText: "Let's go!" }
      },
      {
        path: 'simulation',
        name: 'prebrief-simulation',
        component: () => import('./views/prebrief-section/StartSimulationView.vue'),
        meta: { icon: 'qr-scan', title: 'Start Simulation', nextText: 'Ready to learn!' }
      }
    ])
  },
  debrief: {
    components: {
      default: () => import('./layout/CourseLayout.vue'),
      pageFooter: () => import('./components/CourseFooter.vue'),
      leftNavFooter: () => import('./components/CourseBuildDialogs.vue')
    },
    path: `${orgPrefix}/debrief/:runId`,
    name: 'debrief',
    redirect: () => ({ name: 'debrief-overview' }),
    beforeEnter: to => {
      const debriefRoutes = appRoutes.debrief.children
      const run = state.runResults[to.params.runId as string]
      debriefRoutes
        .filter(x => x.name !== 'debrief-overview')
        .forEach(x => {
          if (!run || !state.user) {
            return
          }
          const userCoursePerformance = run.courseId ? currentUserCoursePerformance(run.courseId) : undefined
          const isCollab = run.multiplayerSessionId !== undefined
          x.meta = {
            ...(x.meta as CourseMeta),
            hidden:
              x.name === 'debrief-lead-placement-analytics' || //Always hide analytics until we implement
              (x.name === 'debrief-performance-history' && (!userCoursePerformance || isCollab)) ||
              (x.name === 'debrief-survey' && !isSurveyAvailable(run))
          }
        })
    },
    meta: { showNavInVuplex: true, navItems: route => courseNavItems('debrief', route) },
    children: createCourseChildren('debrief', [
      {
        path: 'survey',
        name: 'debrief-survey',
        component: () => import('./views/debrief-section/SurveyView.vue'),
        beforeEnter: to =>
          isSurveyAvailable(state.runResults[to.params.runId as string])
            ? true
            : { name: 'debrief-overview', params: to.params },
        meta: {
          icon: 'note',
          title: 'Survey',
          nextText: 'Submit',
          hideRetry: true,
          showSkip: true,
          loader: to => recordSurveyPrompt(to.params.runId as string)
        }
      },
      {
        path: 'overview',
        name: 'debrief-overview',
        component: () => import('./views/debrief-section/Overview.vue'),
        meta: { icon: 'trophy', title: 'Overview', fillHeight: true }
      },
      {
        path: 'analytics',
        name: 'debrief-lead-placement-analytics',
        component: () => import('./views/debrief-section/LeadPlacementView.vue'),
        beforeEnter: to => {
          return {
            name: 'debrief-breakdown-by-state',
            params: to.params
          }
        },
        meta: { icon: 'bar-chart', title: 'Lead Placement Analytics' }
      },
      {
        path: 'breakdown',
        name: 'debrief-breakdown-by-state',
        component: () => import('./views/debrief-section/BreakdownByStageView.vue'),
        meta: { icon: 'clipboard', title: 'Breakdown by Stage' }
      },
      {
        path: 'reflection',
        name: 'debrief-reflection-questions',
        component: () => import('./views/debrief-section/ReflectionQuestionsView.vue'),
        meta: { icon: 'bulb-question', title: 'Reflection Questions' }
      },
      {
        path: 'domain',
        name: 'debrief-domain-scores',
        component: () => import('./views/debrief-section/DomainScoresView.vue'),
        meta: { icon: 'star', title: 'Domain Scores' }
      },
      {
        path: 'history',
        name: 'debrief-performance-history',
        component: () => import('./views/debrief-section/PerformanceHistoryView.vue'),
        meta: { icon: 'history', title: 'Performance History' }
      }
    ])
  },
  qr: {
    name: 'qr',
    path: '/qr',
    component: () => import('./views/QrView.vue')
  }
})

function briefSectionPages(section: BriefSection, currentName: string | symbol | null | undefined) {
  const pages = (appRoutes[section].children as OssoCourseRoute[]).filter(x => x.name && !x.meta.hidden)
  return { pages, currentIndex: pages.findIndex(x => x.name === currentName) }
}

export function courseNavigation(
  section: BriefSection,
  currentName: string | symbol | null | undefined
): { prev?: OssoCourseRoute; next?: OssoCourseRoute } {
  const { pages, currentIndex } = briefSectionPages(section, currentName)
  if (currentIndex < 0) {
    return {}
  }
  return { prev: pages[currentIndex - 1], next: pages[currentIndex + 1] }
}

function courseNavItems(section: BriefSection, route: RouteLocationNormalizedLoadedGeneric): NavItem[] {
  const { pages, currentIndex } = briefSectionPages(section, route.name)
  return pages.map((x, i) => ({
    label: x.meta.title,
    icon: x.meta.icon,
    route: x,
    testId: `nav-${String(x.name)}`,
    completed: i < currentIndex
  }))
}

function adminNavItems(): NavItem[] {
  return Object.values(adminRoutes.admin.children).map(x => ({
    label: x.meta.title,
    icon: x.meta.icon,
    route: x,
    hide: x.meta.hide?.(),
    testId: `nav-${x.name}`,
    badge: x.meta.badge
  }))
}

export const adminCourseRoutes = createRoutes('admin', {
  'admin-course-details': {
    path: 'details',
    name: 'admin-course-details',
    component: () => import('./admin/views/Course/DetailsView.vue'),
    meta: { icon: 'edit', title: 'Details' }
  },
  'admin-course-organizations': {
    path: 'organizations',
    name: 'admin-course-organizations',
    component: () => import('./admin/views/Course/OrganizationsView.vue'),
    meta: { icon: 'people', title: 'Organizations' }
  }
})

export const adminTechniqueRoutes = createRoutes('admin', {
  'admin-technique-details': {
    path: 'details',
    name: 'admin-technique-details',
    component: () => import('./admin/views/Technique/DetailsView.vue'),
    meta: { icon: 'edit', title: 'Details' }
  },
  'admin-technique-prebrief': {
    path: 'prebrief',
    name: 'admin-technique-prebrief',
    component: () => import('./admin/views/Technique/PrebriefView.vue'),
    meta: { icon: 'editClipboard', title: 'Prebrief' },
    beforeEnter: async to => {
      const techniqueId = to.params.id as string
      const id = prebriefId(techniqueId)
      const request = { type: 'Prebrief', ids: [id] }
      const prebriefTask = postGetBody<Prebrief[]>('admin/documents-by-id', request).then(prebriefs => {
        if (prebriefs?.[0]) {
          state.prebriefs[id] = prebriefs?.[0]
        }
      })
      if (!state.prebriefs[id]) {
        await prebriefTask
      }
    }
  }
})

const moduleBuildOf = (route: { params: RouteParamsGeneric }) =>
  route.params.techniqueId
    ? findEmbeddedModule(route.params.id as string, route.params.techniqueId as string)
    : state.linkedModuleBuilds[route.params.id as string]
const moduleBuildProps = (to: { params: RouteParamsGeneric }) => ({ moduleBuild: moduleBuildOf(to) })
const currentModuleHasFlow = () => !!moduleBuildOf(router.currentRoute.value)?.flow

export const adminModuleBuildRoutes = createRoutes('admin', {
  'admin-module-build-details': {
    path: 'details',
    name: 'admin-module-build-details',
    component: () => import('./admin/views/LinkedModuleBuilds/DetailsView.vue'),
    meta: { icon: 'edit', title: 'Details' }
  },
  'admin-module-build-flow': {
    path: 'flow',
    name: 'admin-module-build-flow',
    component: () => import('./admin/views/LinkedModuleBuilds/FlowView.vue'),
    props: moduleBuildProps,
    meta: { icon: 'layers', title: 'Flow', hide: () => !currentModuleHasFlow() }
  },
  'admin-module-build-rubric': {
    path: 'rubric',
    name: 'admin-module-build-rubric',
    component: () => import('./admin/views/LinkedModuleBuilds/RubricView.vue'),
    props: moduleBuildProps,
    meta: { icon: 'star', title: 'Rubric', hide: () => !currentModuleHasFlow() }
  },
  'admin-module-build-stages': {
    path: 'stages',
    name: 'admin-module-build-stages',
    component: () => import('./admin/views/LinkedModuleBuilds/StagesView.vue'),
    props: moduleBuildProps,
    meta: { icon: 'clipboard', title: 'Stages', hide: currentModuleHasFlow }
  },
  'admin-module-build-courses': {
    path: 'courses',
    name: 'admin-module-build-courses',
    component: () => import('./admin/views/LinkedModuleBuilds/CoursesView.vue'),
    meta: { icon: 'school-cap', title: 'Courses' }
  }
})

export const adminModuleBuildTabs = {
  details: adminModuleBuildRoutes['admin-module-build-details'],
  flow: adminModuleBuildRoutes['admin-module-build-flow'],
  rubric: adminModuleBuildRoutes['admin-module-build-rubric'],
  stages: adminModuleBuildRoutes['admin-module-build-stages'],
  courses: adminModuleBuildRoutes['admin-module-build-courses']
}

export const adminPlayerBuildRoutes = createRoutes('admin', {
  'admin-player-build-details': {
    path: 'details',
    name: 'admin-player-build-details',
    component: () => import('./admin/views/LinkedPlayerBuilds/DetailsView.vue'),
    meta: { icon: 'edit', title: 'Details' }
  },
  'admin-player-build-embedded-modules': {
    path: 'embedded',
    name: 'admin-player-build-embedded-modules',
    component: () => import('./admin/views/LinkedPlayerBuilds/EmbeddedModulesView.vue'),
    meta: { icon: 'webBox', title: 'Embedded Modules' }
  }
})

export const adminEmbeddedModuleRoutes = createRoutes('admin', {
  'admin-player-build-embedded-module-details': {
    path: 'details',
    name: 'admin-player-build-embedded-module-details',
    component: () => import('./admin/views/LinkedPlayerBuilds/EmbeddedModuleDetailsView.vue'),
    meta: { icon: 'edit', title: 'Details' }
  },
  'admin-player-build-embedded-module-flow': {
    path: 'flow',
    name: 'admin-player-build-embedded-module-flow',
    component: () => import('./admin/views/LinkedModuleBuilds/FlowView.vue'),
    props: moduleBuildProps,
    meta: { icon: 'layers', title: 'Flow', hide: () => !currentModuleHasFlow() }
  },
  'admin-player-build-embedded-module-rubric': {
    path: 'rubric',
    name: 'admin-player-build-embedded-module-rubric',
    component: () => import('./admin/views/LinkedModuleBuilds/RubricView.vue'),
    props: moduleBuildProps,
    meta: { icon: 'star', title: 'Rubric', hide: () => !currentModuleHasFlow() }
  },
  'admin-player-build-embedded-module-stages': {
    path: 'stages',
    name: 'admin-player-build-embedded-module-stages',
    component: () => import('./admin/views/LinkedModuleBuilds/StagesView.vue'),
    props: moduleBuildProps,
    meta: { icon: 'clipboard', title: 'Stages', hide: currentModuleHasFlow }
  }
})

export const adminEmbeddedModuleTabs = {
  details: adminEmbeddedModuleRoutes['admin-player-build-embedded-module-details'],
  flow: adminEmbeddedModuleRoutes['admin-player-build-embedded-module-flow'],
  rubric: adminEmbeddedModuleRoutes['admin-player-build-embedded-module-rubric'],
  stages: adminEmbeddedModuleRoutes['admin-player-build-embedded-module-stages']
}

function replacedTabRedirect(
  module: { flow?: Flow },
  to: RouteLocationNormalizedGeneric,
  tabs: { flow: { name: string }; rubric: { name: string }; stages: { name: string } }
): RouteLocationRaw | undefined {
  const replacementTabs: { [name: string]: string } = module.flow
    ? { [tabs.stages.name]: tabs.flow.name }
    : {
        [tabs.flow.name]: tabs.stages.name,
        [tabs.rubric.name]: tabs.stages.name
      }
  const replacementTab = replacementTabs[to.name as string]
  return replacementTab ? { name: replacementTab, params: to.params } : undefined
}

export const adminRoutes = createRoutes('admin', {
  // Prevents error in tests and running locally when navigating to portal
  portal: {
    path: '/portal/:orgId?',
    component: { render: () => null }
  },
  admin: {
    beforeEnter: async () => {
      await initialUpdate
      if (!isAdmin.value) {
        return { name: 'root' }
      }
    },
    redirect: () => {
      return { name: 'admin-installed' }
    },
    path: '/admin',
    component: () => import('./admin/Provider.vue'),
    meta: { showNavInVuplex: true, navItems: adminNavItems },
    children: [
      {
        path: 'installed',
        name: 'admin-installed',
        component: () => import('./admin/views/InstalledModulesView.vue'),
        meta: { icon: 'packageDown', title: 'Installed Modules' }
      },
      {
        path: 'module-builds',
        name: 'admin-module-builds',
        redirect: to => ({ name: 'admin-module-builds-list', query: to.query }),
        meta: { icon: 'webBox', title: 'Module Builds' },
        children: [
          {
            path: '',
            name: 'admin-module-builds-list',
            component: () => import('./admin/views/ModuleBuildsListView.vue')
          },
          {
            path: ':id',
            name: 'admin-module-build',
            redirect: to => ({ name: 'admin-module-build-details', params: to.params }),
            meta: {
              tabs: true,
              loader: async to => {
                await Promise.all([initialUpdate, initialAdminUpdate])
                const id = to.params.id as string
                await ensureLinkedModuleBuilds([id])
                const linkedModuleBuild = state.linkedModuleBuilds[id]
                if (linkedModuleBuild) {
                  return replacedTabRedirect(linkedModuleBuild, to, adminModuleBuildTabs)
                }
                await ensureLinkedModuleBuildsByModuleBuildId([id])
                const link = linkedModuleBuildsByModuleBuildId.value[id]
                if (link) {
                  return { name: 'admin-module-build', params: { id: link.id } }
                }
                return { name: 'admin-module-builds-list' }
              }
            },
            children: Object.values(adminModuleBuildRoutes)
          }
        ]
      },
      {
        path: 'courses',
        name: 'admin-courses',
        redirect: to => ({ name: 'admin-courses-list', query: to.query }),
        meta: { icon: 'school-cap', title: 'Courses' },
        children: [
          {
            path: '',
            name: 'admin-courses-list',
            meta: { loader: () => initialAdminUpdate },
            component: () => import('./admin/views/CoursesListView.vue')
          },
          {
            path: ':id',
            name: 'admin-course',
            redirect: to => ({ name: 'admin-course-details', params: to.params }),
            meta: {
              tabs: true,
              loader: async to => {
                await initialAdminUpdate
                const course = adminState.courses[to.params.id as string]
                if (!course) {
                  return { name: 'admin-courses-list' }
                }
                if (course.linkedModuleBuildsId) {
                  await ensureLinkedModuleBuilds([course.linkedModuleBuildsId])
                }
              }
            },
            children: Object.values(adminCourseRoutes)
          }
        ]
      },
      {
        path: 'multiplayer',
        name: 'admin-multiplayer',
        component: () => import('./admin/views/MultiplayerSessionsListView.vue'),
        meta: { icon: 'multiplayer', title: 'Multiplayer' }
      },
      {
        path: 'run-analytics',
        name: 'admin-run-analytics',
        component: () => import('./admin/views/RunAnalyticsView.vue'),
        meta: {
          icon: 'bar-chart',
          title: 'Run Analytics',
          badge: 'Beta',
          hide: () => !hasAdminPermission('Runs.View'),
          loader: async () => {
            if (!hasAdminPermission('Runs.View')) {
              return { name: 'admin-runs-list' }
            }
            await initialAdminUpdate
          }
        }
      },
      {
        path: 'runs',
        name: 'admin-runs',
        redirect: to => ({ name: 'admin-runs-list', query: to.query }),
        meta: { icon: 'runs', title: 'Runs' },
        children: [
          {
            path: '',
            name: 'admin-runs-list',
            component: () => import('./admin/views/RunsListView.vue')
          },
          {
            path: ':id/details',
            name: 'admin-run-details',
            component: () => import('./admin/views/RunDetailsView.vue'),
            beforeEnter: async to => {
              const runId = to.params.id as string
              if (!adminState.playerRuns[runId]) {
                const request = { type: 'PlayerRun', ids: [runId] }
                const runResponse = await postGetBody<AdminPlayerRun[]>('admin/documents-by-id', request)
                const adminPlayerRun = runResponse?.[0]
                if (adminPlayerRun) {
                  adminState.playerRuns[runId] = adminPlayerRun
                } else {
                  return { name: 'admin-runs-list' }
                }
              }
              const run = adminState.playerRuns[runId]
              await Promise.all([
                run.linkedPlayerBuildsId ? ensureLinkedPlayerBuilds([run.linkedPlayerBuildsId]) : Promise.resolve(),
                run.linkedModuleBuildsId ? ensureLinkedModuleBuilds([run.linkedModuleBuildsId]) : Promise.resolve(),
                ensureUserSlims([userSlimId(run.userId)])
              ])
            }
          }
        ]
      },
      {
        path: 'techniques',
        name: 'admin-techniques',
        redirect: () => ({ name: 'admin-techniques-list' }),
        meta: { icon: 'clipboard', title: 'Techniques' },
        children: [
          {
            path: '',
            name: 'admin-techniques-list',
            component: () => import('./admin/views/TechniquesListView.vue'),
            meta: { icon: 'clipboard', title: 'Techniques' }
          },
          {
            path: ':id',
            name: 'admin-technique',
            meta: { icon: 'clipboard', title: 'Technique Details', tabs: true },
            redirect: () => ({ name: 'admin-technique-details' }),
            beforeEnter: async to => {
              const techniqueId = to.params.id as string
              const request = { type: 'PlayerTechnique', ids: [techniqueId] }
              const playerTechniqueTask = postGetBody<PlayerTechnique[]>('admin/documents-by-id', request).then(
                playerTechniques => {
                  if (playerTechniques?.[0]) {
                    state.playerTechniques[techniqueId] = playerTechniques?.[0]
                  }
                }
              )
              if (!state.playerTechniques[techniqueId]) {
                await playerTechniqueTask
              }
            },
            children: Object.values(adminTechniqueRoutes)
          }
        ]
      },
      {
        path: 'player-builds',
        name: 'admin-player-builds',
        redirect: to => ({ name: 'admin-player-builds-list', query: to.query }),
        meta: { icon: 'player-builds', title: 'Player Builds' },
        children: [
          {
            path: '',
            name: 'admin-player-builds-list',
            component: () => import('./admin/views/LinkedPlayerBuildsListView.vue'),
            meta: { icon: 'player-builds', title: 'Player Builds' }
          },
          {
            path: ':id',
            name: 'admin-player-build',
            redirect: to => ({ name: 'admin-player-build-details', params: to.params }),
            meta: {
              tabs: true,
              loader: async to => {
                const id = to.params.id as string
                await Promise.all([ensureLinkedPlayerBuilds([id]), ensureLinkedPlayerBuildsSettings([id])])
                if (state.linkedPlayerBuilds[id]) {
                  return
                }
                await ensureLinkedPlayerBuildsByPlayerBuildId([id])
                const link = linkedPlayerBuildsByPlayerBuildId.value[id]
                if (link) {
                  return { name: 'admin-player-build', params: { id: link.id } }
                }
                return { name: 'admin-player-builds-list' }
              }
            },
            children: Object.values(adminPlayerBuildRoutes)
          },
          {
            path: ':id/embedded/:techniqueId',
            name: 'admin-player-build-embedded-module',
            redirect: to => ({ name: 'admin-player-build-embedded-module-details', params: to.params }),
            meta: {
              tabs: true,
              loader: async to => {
                const id = to.params.id as string
                await ensureLinkedPlayerBuilds([id])
                const embeddedModule = findEmbeddedModule(id, to.params.techniqueId as string)
                if (!embeddedModule) {
                  return { name: 'admin-player-build', params: { id } }
                }
                return replacedTabRedirect(embeddedModule, to, adminEmbeddedModuleTabs)
              }
            },
            children: Object.values(adminEmbeddedModuleRoutes)
          }
        ]
      },
      {
        path: 'users',
        name: 'admin-users',
        redirect: to => ({ name: 'admin-users-list', query: to.query }),
        meta: {
          icon: 'people',
          title: 'Users',
          hide: () => !hasAdminPermission('Users')
        },
        children: [
          {
            path: '',
            name: 'admin-users-list',
            component: () => import('./admin/views/UsersListView.vue'),
            beforeEnter: async () => {
              if (!hasAdminPermission('Users')) {
                return { name: 'admin-module-builds' }
              }
              await initialAdminUpdate
            }
          },
          {
            path: 'add',
            name: 'admin-user-add',
            component: () => import('./admin/views/UserDetailView.vue'),
            beforeEnter: () => {
              if (!hasAdminPermission('Users') || !hasAdminPermission('Organizations.Switch.All')) {
                return { name: 'admin-users-list' }
              }
            }
          },
          {
            path: ':id/details',
            name: 'admin-user-details',
            component: () => import('./admin/views/UserDetailView.vue'),
            beforeEnter: () => {
              if (!hasAdminPermission('Users')) {
                return { name: 'admin-module-builds' }
              }
            },
            meta: {
              loader: async to => {
                const id = to.params.id as string
                await Promise.all([ensureUser(id), loadHealthStreamStudents(id)])
                if (!adminState.users[id]) {
                  return { name: 'admin-users-list' }
                }
              }
            }
          }
        ]
      }
    ]
  }
})

export const authRoutes = createRoutes('auth', {
  login: {
    path: '/login',
    component: () => import('./auth/views/LoginOptionsView.vue'),
    beforeEnter: to => {
      if (!state.vuplex?.qrReaderAvailable) {
        return { name: 'loginIdp', query: to.query }
      }
    }
  },
  loginIdp: {
    path: '/login-idp',
    beforeEnter: to => {
      if (!config.enableHealthStreamLogin) {
        return { name: 'loginOsso' }
      }
      if (state.vuplex) {
        to.meta.backRoute = authRoutes.login
      }
    },
    component: () => import('./auth/views/LoginIdpView.vue')
  },
  loginOsso: {
    path: '/login-osso',
    component: () => import('./auth/views/OssoLoginView.vue'),
    beforeEnter: to => {
      to.meta.backRoute = config.enableHealthStreamLogin
        ? authRoutes.loginIdp
        : state.vuplex?.qrReaderAvailable
          ? authRoutes.login
          : undefined
    }
  },
  resetPassword: {
    path: '/reset-password',
    component: () => import('./auth/views/ResetPassword.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.loginOsso
    }
  },
  loginQrHelp: {
    path: '/login-qr-help',
    component: () => import('./auth/views/LoginQrHelpView.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.login
    }
  },
  verifyQrCode: {
    path: '/verify-qr-code',
    component: () => import('./auth/views/VerifyQrCode.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.login
    }
  },
  permissionsDenied: {
    path: '/permissions-denied',
    component: () => import('./auth/views/PermissionsDenied.vue')
  },
  healthStreamMissingLogininfo: {
    path: '/healthstream-first-login-failure',
    component: () => import('./auth/views/HealthStreamMissingLoginInfoView.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.login
    }
  }
})

const redirects: RouteRecordRaw[] = [
  {
    path: '/healthstream-course',
    redirect: to => {
      const healthStreamOrganizationId = to.query['org-id'] as string
      const healthStreamCourseId = to.query['course-id'] as string

      const redirect = router.resolve({
        name: 'prebrief',
        params: { organizationId: healthStreamOrganizationId, courseId: healthStreamCourseId },
        query: { healthstream: 'true' }
      })

      localStorage.setItem('redirect', redirect.href)
      localStorage.setItem('healthStreamOrganizationId', healthStreamOrganizationId)
      localStorage.setItem('healthStreamCourseId', healthStreamCourseId)
      localStorage.setItem('healthStreamOssoCourseId', to.query['osso-course-id'] as string)
      localStorage.setItem('healthStreamUserId', to.query['user-id'] as string)

      loginWithHealthstream()
      // Early return to prevent redirect from navigating before health stream redirect
      return undefined as unknown as RouteLocationRaw
    },
    meta: { externalAuth: true }
  },
  {
    path: '/healthstream',
    component: { render: () => null },
    beforeEnter: async to => {
      const authCode = checkForValidAuthCode(to.query)
      if (authCode) {
        const path = await handleHealthStreamAuthCode(authCode)
        firstNavigation = false
        authenticateFinished()
        if (path.startsWith('/portal')) {
          location.href = path
          return undefined as unknown as RouteLocationRaw
        }
        return router.resolve(path)
      }
      return { name: '' }
    },
    meta: { externalAuth: true }
  }
]

function createRoutes<T extends { [K in keyof T]: OssoRoute | OssoCourseRoute }>(
  layout: Layout,
  routes: { [K in keyof T]: T[K] }
) {
  return Object.keys(routes).reduce(
    (acc, key) => {
      const route = routes[key as keyof T]
      const meta = { ...route.meta, layout }
      acc[key as keyof T] = { ...route, name: key, meta }
      return acc
    },
    <{ [K in keyof T]: T[K] & { name: string; meta: RouteMeta & { layout: Layout } } }>{}
  )
}

type CourseChildRoute = Omit<RouteRecordSingleView, 'meta'> & {
  meta: Omit<CourseMeta, 'section'>
}
function createCourseChildren(section: BriefSection, children: CourseChildRoute[]): OssoRoute[] {
  return children.map(x => ({
    ...x,
    meta: {
      ...x.meta,
      section
    }
  }))
}

export const router = createRouter({
  history: createWebHistory('/'),
  routes: [...redirects, ...Object.values(appRoutes), ...Object.values(adminRoutes), ...Object.values(authRoutes)]
})

registerNavigationLoading(router)

let firstNavigation = true
router.beforeEach(async (to, from) => {
  await checkForUpdate(to, from)

  if (to.meta.externalAuth) {
    return
  }

  const authRedirect = await handleFirstNavigation(to)
  if (shouldUseHealthStreamLogin(to, authRedirect)) {
    localStorage.setItem('redirect', stripHsLoginQuery(to.fullPath))
    loginWithHealthstream()
    return undefined as unknown as RouteLocationRaw
  }

  if (shouldHandoffToPortal(to, authRedirect)) {
    location.href = stripHsLoginQuery(to.fullPath)
    return undefined as unknown as RouteLocationRaw
  }

  handleAdminNavigation()
  const appRedirect = await handleAppNavigation(to.path)
  if (appRedirect) {
    return appRedirect
  }

  if (to.meta.hideOnWeb && !state.vuplex) {
    return '/'
  }

  if (!state.permissionsAccepted && shouldRouteToPermissionsDenied(to.name)) {
    return authRoutes.permissionsDenied
  }

  if (authRedirect) {
    return authRedirect
  }

  const redirect = to.meta.loader ? await to.meta.loader(to) : undefined
  if (redirect) return redirect
})

function shouldUseHealthStreamLogin(to: RouteLocationNormalizedGeneric, authRedirect?: RouteLocationRaw) {
  return hasHsLoginQuery(to) && authRedirect === authRoutes.login.path
}

function shouldHandoffToPortal(to: RouteLocationNormalizedGeneric, authRedirect?: RouteLocationRaw) {
  return hasHsLoginQuery(to) && !authRedirect && to.path.startsWith('/portal')
}

function hasHsLoginQuery(to: RouteLocationNormalizedGeneric) {
  return 'hslogin' in to.query
}

function stripHsLoginQuery(fullPath: string) {
  const url = new URL(fullPath, window.location.origin)
  url.searchParams.delete('hslogin')
  const query = url.searchParams.toString()
  // /portal/:orgId? matches /portal/ as orgId="" — collapse to /portal so the optional segment is treated as absent
  const path = url.pathname === '/portal/' ? '/portal' : url.pathname
  return path + (query ? `?${query}` : '')
}

async function handleFirstNavigation(to: RouteLocationNormalizedGeneric) {
  if (!firstNavigation) {
    return undefined
  }
  firstNavigation = false
  await initQuestBridge()
  await setStateFromCache()
  await setAdminStateFromCache()
  // this needs to go here rather than app onMounted because beforeEnter guards run before that
  // and we want to ensure required data is loaded to do routing checks, eg entity exists
  const authPath = await authenticate(to as OssoRouteLocation)
  authenticateFinished()
  if (state.user) {
    await ensureCurrentLinkedPlayerBuilds()
  }
  return authPath === to.fullPath ? undefined : authPath
}

// TODO: playwright test for this
async function checkForUpdate(to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric) {
  if (
    localStorage.getItem('swNeedsUpdate') &&
    from.name != null &&
    to.meta?.layout !== 'auth' &&
    !navigator.webdriver
  ) {
    localStorage.removeItem('swNeedsUpdate')
    await updateGlobalServiceWorker()
    location.href = to.fullPath
    return
  }
}
